import paystackConfig from '../config/paystack.config';
import { getUser } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL;

class PaystackService {
  constructor() {
    this.config = paystackConfig;
    this.scriptLoaded = false;
  }

  loadPaystackScript() {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded || window.PaystackPop) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.async = true;
      script.onload = () => { this.scriptLoaded = true; resolve(); };
      script.onerror = () => reject(new Error('Failed to load Paystack script'));
      document.body.appendChild(script);
    });
  }

  async initializePayment(paymentData) {
    await this.loadPaystackScript();
    const user = getUser();
    if (!user) throw new Error('User not authenticated');
    const config = this.buildPaymentConfig(paymentData, user);
    return new Promise((resolve, reject) => {
      const handler = window.PaystackPop.setup({
        ...config,
        onClose: () => reject(new Error('Payment cancelled by user')),
        callback: (response) => resolve(response),
      });
      handler.openIframe();
    });
  }

  buildPaymentConfig(paymentData, user) {
    const { amount, email, causeId, causeName, reference, metadata = {} } = paymentData;
    return {
      key:      this.config.publicKey,
      email:    email || user.email,
      amount:   Math.round(parseFloat(amount) * 100),
      currency: this.config.currency,
      ref:      reference || this.generateReference(),
      channels: this.config.channels,
      label:    this.config.ui.title,
      metadata: {
        user_id:         String(user.id   || ''),
        user_email:      String(user.email || ''),
        cause_id:        String(causeId   || ''),
        cause_name:      String(causeName || ''),
        donation_amount: String(amount    || ''),
        ...metadata,
      },
    };
  }

  buildSubscriptionConfig(subscriptionData, user) {
    const { amount, billingCycle, planName, reference } = subscriptionData;
    return {
      key:      this.config.publicKey,
      email:    user.email,
      amount:   Math.round(parseFloat(amount) * 100),
      currency: this.config.currency,
      ref:      reference || this.generateReference('SUB'),
      channels: this.config.channels,
      label:    'BestieConnect Premium',
      metadata: {
        user_id:       String(user.id    || ''),
        plan:          String(planName   || 'premium'),
        billing_cycle: String(billingCycle || 'monthly'),
        payment_type:  'subscription',
      },
    };
  }

  generateReference(prefix = 'DN') {
    return `BESTIE-${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
  }

  async verifyPayment(reference) {
    const response = await fetch(`${API_URL}/donations/verify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: JSON.stringify({ reference }),
    });
    if (!response.ok) throw new Error('Payment verification failed');
    return response.json();
  }

  async processDonation(donationData, onSuccess, onError) {
    try {
      const paymentResponse = await this.initializePayment(donationData);
      if (paymentResponse.status === 'success') {
        const verificationResult = await this.verifyPayment(paymentResponse.reference);
        if (verificationResult.success) {
          onSuccess && onSuccess(verificationResult);
          return verificationResult;
        }
        throw new Error('Payment verification failed');
      }
      throw new Error('Payment was not successful');
    } catch (error) {
      onError && onError(error);
      throw error;
    }
  }

  async processSubscription(subscriptionData, onSuccess, onError) {
    try {
      await this.loadPaystackScript();
      const user = getUser();
      if (!user) throw new Error('User not authenticated');
      const config = this.buildSubscriptionConfig(subscriptionData, user);
      const response = await new Promise((resolve, reject) => {
        const handler = window.PaystackPop.setup({
          ...config,
          onClose:  () => reject(new Error('Payment window closed')),
          callback: (res) => resolve(res),
        });
        handler.openIframe();
      });
      onSuccess && onSuccess(response);
    } catch (error) {
      onError && onError(error);
    }
  }

  getPaymentChannels()        { return this.config.channels; }
  getPresetAmounts()          { return this.config.presetAmounts; }
  formatCurrency(amount)      {
    return new Intl.NumberFormat('en-NG', { style:'currency', currency:'NGN', minimumFractionDigits:0 }).format(amount);
  }
  validateDonationAmount(amount) {
    const n = parseFloat(amount);
    if (isNaN(n))                      return { valid:false, error:'Please enter a valid amount' };
    if (n < this.config.minAmount)     return { valid:false, error:`Minimum amount is ₦${this.config.minAmount}` };
    if (n > this.config.maxAmount)     return { valid:false, error:`Maximum amount is ₦${this.config.maxAmount.toLocaleString()}` };
    return { valid:true };
  }
  getMinAmount() { return this.config.minAmount; }
  getMaxAmount() { return this.config.maxAmount; }
}

const paystackService = new PaystackService();
export default paystackService;
